import React, { Fragment, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { cloneDeep, delay, removeTrailingSlash, updateNodeState } from '../../../../../utils';

import SimpleFormCheck from '../../../../General/Forms/SimpleFormCheck';

import { initialSellingGroupFilterOptions, setSubscriberSellingGroupFilterOptions } from '../actions';

const SellingGroupFilterChoose = (props) => {
     const { classNameWrap } = props;

     const [searchParams, setSearchParams] = useSearchParams();

     const dispatch = useDispatch();
     const sellingGroupFilterOptions = useSelector((state) => state.subscriber.sellingGroupFilterOptions);

     const accordionRefs = useRef([]);
     const [expanded, setExpanded] = useState([]);

     const sellingGroupFilterOptionList = useMemo(() => {
          const _sellingGroupFilterOptionList = {};

          Object.entries(sellingGroupFilterOptions).forEach(([category, items]) => {
               if (items.length) {
                    _sellingGroupFilterOptionList[category] = items;
               }
          });

          return _sellingGroupFilterOptionList;
     }, [sellingGroupFilterOptions]);

     const initSelectedFilterOptionsFromSearchParams = (filterOptions) => {
          if (!searchParams.size) return;
          const newSellingGroupFilterOptions = cloneDeep(filterOptions);

          Object.keys(newSellingGroupFilterOptions).forEach((key) => {
               const values = searchParams.getAll(key).map((item) => removeTrailingSlash(item));
               values.forEach((value) => {
                    const currentGroup = cloneDeep(newSellingGroupFilterOptions[key]);

                    const checked = true;
                    newSellingGroupFilterOptions[key] = updateNodeState(currentGroup, value, checked);
               });
          });
          dispatch(setSubscriberSellingGroupFilterOptions(newSellingGroupFilterOptions));
     };

     const fetchSellingGroupFilterOptions = async () => {
          const _sellingGroupFilterOptions = await dispatch(initialSellingGroupFilterOptions());
          if (_sellingGroupFilterOptions) {
               initSelectedFilterOptionsFromSearchParams(_sellingGroupFilterOptions);
          }
     };

     // Init expanded filter options
     useLayoutEffect(() => {
          const keys = Object.keys(sellingGroupFilterOptions).map((_, index) => index);
          setExpanded(keys);
          accordionRefs.current = keys.map(() => React.createRef());
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          fetchSellingGroupFilterOptions();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const handleToggleExpand = async (index) => {
          const accordionBody = accordionRefs.current[index].current;

          if (!expanded.includes(index)) {
               accordionBody.style.height = `${accordionBody.scrollHeight}px`;
               setExpanded((prevExpanded) => [...prevExpanded, index]);
               return;
          }

          if (!accordionBody.style.height) {
               accordionBody.style.height = `${accordionBody.scrollHeight}px`;
          }

          await delay(10);

          accordionBody.style.height = '';
          const newExpanded = expanded.filter((expanded) => expanded !== index);
          setExpanded(newExpanded);
     };

     const handleCheckboxChange = (groupKey, nodeValue, checked) => {
          // Create a deep copy of the current state to avoid mutation
          const currentGroup = cloneDeep(sellingGroupFilterOptions[groupKey]);

          // Update the state with the new changes
          const selectedFilters = {
               ...sellingGroupFilterOptions,
               [groupKey]: updateNodeState(currentGroup, nodeValue, checked),
          };

          dispatch(setSubscriberSellingGroupFilterOptions(selectedFilters));

          // Update the URL search params
          const paramSearch = new URLSearchParams(searchParams);
          if (checked) {
               paramSearch.append(groupKey, nodeValue);
          } else {
               paramSearch.delete(groupKey, nodeValue);
               paramSearch.delete(groupKey, `${nodeValue}/`);
          }

          const newSearchParams = paramSearch.toString().replace(/%2F$/, '');
          setSearchParams(newSearchParams);
     };

     const renderCheckbox = (node, groupKey, level = 0) => {
          const hasChildren = node.children?.length > 0;
          return (
               <Fragment key={node.value}>
                    <SimpleFormCheck
                         id={node.value}
                         label={node.label}
                         checked={!!node.checked}
                         indeterminate={!!node.indeterminate}
                         className={classNames({ 'has-child': hasChildren })}
                         onChange={(e) => handleCheckboxChange(groupKey, node.value, e.target.checked)}
                    />
                    {hasChildren && (
                         <div className="selling-group-filter-accordion-nested">
                              {node.children.map((child) => renderCheckbox(child, groupKey, level + 1))}
                         </div>
                    )}
               </Fragment>
          );
     };

     return (
          <div className={classNames('selling-group-filter-choose', { [classNameWrap]: classNameWrap })}>
               <div className="selling-group-filter-accordion">
                    {Object.entries(sellingGroupFilterOptionList).map(([category, items], index) => (
                         <div key={category} className={classNames('selling-group-filter-accordion-item', { show: expanded.includes(index) })}>
                              <div className="selling-group-filter-accordion-header" onClick={() => handleToggleExpand(index)}>
                                   <button className="selling-group-filter-accordion-button">
                                        <span>{category}</span>
                                        <div className="selling-group-filter-accordion-button-icon">
                                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path
                                                       d="M12.0001 13.4102L11.6465 13.0566L7.695 9.10507C7.69498 9.10505 7.69496 9.10504 7.69494 9.10502C7.56368 8.97382 7.38569 8.90012 7.2001 8.90012C7.01453 8.90012 6.83656 8.9738 6.7053 9.10497C6.70527 9.105 6.70523 9.10504 6.7052 9.10507M12.0001 13.4102L6.3517 10.4483C6.12673 10.2232 6.00035 9.91806 6.00035 9.59987C6.00035 9.28167 6.12673 8.9765 6.3517 8.75147L6.7052 9.10507M12.0001 13.4102L12.3537 13.0566L16.3018 9.1085C16.4335 8.98264 16.6091 8.91305 16.7914 8.91464C16.975 8.91623 17.1505 8.98985 17.2803 9.11964C17.4101 9.24942 17.4837 9.42499 17.4853 9.60853C17.4869 9.79081 17.4173 9.96647 17.2915 10.0982L12.495 14.8947C12.495 14.8947 12.495 14.8947 12.4949 14.8947C12.3637 15.0259 12.1857 15.0996 12.0001 15.0996C11.8145 15.0996 11.6365 15.0259 11.5053 14.8947C11.5052 14.8947 11.5052 14.8947 11.5052 14.8947L6.7053 10.0948C6.70529 10.0947 6.70527 10.0947 6.70525 10.0947M12.0001 13.4102L6.70525 10.0947M6.7052 9.10507C6.57403 9.23633 6.50035 9.4143 6.50035 9.59987C6.50035 9.78545 6.57405 9.96345 6.70525 10.0947M6.7052 9.10507L6.70525 10.0947"
                                                       fill="currentColor"
                                                       stroke="currentColor"
                                                  />
                                             </svg>
                                        </div>
                                   </button>
                              </div>
                              <div ref={accordionRefs.current[index]} className="selling-group-filter-accordion-collapse">
                                   <div className="selling-group-filter-accordion-body">
                                        {items.map((filter) => renderCheckbox(filter, category))}
                                   </div>
                              </div>
                         </div>
                    ))}
               </div>
          </div>
     );
};

SellingGroupFilterChoose.propTypes = {
     classNameWrap: PropTypes.string,
};

SellingGroupFilterChoose.defaultProps = {};

export default memo(SellingGroupFilterChoose);
